import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import React from 'react';
import { closeoutTestId } from './test-ids';

export type ReopenPeriodAlertData = {
  range?: { startDate?: string; endDate?: string; uuid?: string | null };
  errors?: number;
  warnings?: number;
  ignores?: number;
  status?: string;
  hasBillingChangesWhileReopened?: boolean;
};

type ReopenPeriodModalProps = {
  open: boolean;
  onClose: () => void;
  alertData: ReopenPeriodAlertData | null;
  onConfirm: (alertData: ReopenPeriodAlertData | null) => void;
  confirmLoading?: boolean;
  confirmError?: string | null;
};

export function isPeriodWithinCurrentOrPreviousMonth(range: {
  startDate?: string;
  endDate?: string;
}): boolean {
  if (!range?.endDate) return false;
  const periodEnd = new Date(range.endDate);
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  const periodYear = periodEnd.getFullYear();
  const periodMonth = periodEnd.getMonth();

  const isCurrentMonth = periodYear === currentYear && periodMonth === currentMonth;
  const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
  const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
  const isPreviousMonth = periodYear === prevYear && periodMonth === prevMonth;

  return isCurrentMonth || isPreviousMonth;
}

const REOPEN_BODY = (
  <>
    Reopening this period will allow Daily Session Notes and payers to be edited.
    <br />
    <br />
    Changes made after reopening may impact billing data and reporting. Are you sure you want to
    reopen this period?
  </>
);

const ReopenPeriodModal: React.FC<ReopenPeriodModalProps> = ({
  open,
  onClose,
  alertData,
  onConfirm,
  confirmLoading = false,
  confirmError = null,
}) => {
  const bodyContent = REOPEN_BODY;

  const handleConfirm = () => {
    onConfirm(alertData);
  };

  return (
    <Dialog
      data-testid={closeoutTestId('reopen-dialog')}
      open={open}
      onClose={onClose}
      aria-labelledby="reopen-period-dialog-title"
      aria-describedby="reopen-period-dialog-description"
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 0,
        },
      }}
    >
      <DialogTitle
        id="reopen-period-dialog-title"
        component="h2"
        sx={{
          fontSize: '1.25rem',
          fontWeight: 600,
          color: 'text.primary',
          pb: 0,
        }}
      >
        Reopen Closed Period?
      </DialogTitle>
      <DialogContent>
        <Typography
          id="reopen-period-dialog-description"
          variant="body2"
          sx={{ color: '#6C737F', fontWeight: 400, pt: 2 }}
          component="div"
        >
          {bodyContent}
        </Typography>
        {confirmError ? (
          <Alert
            severity="error"
            sx={{ mt: 2 }}
          >
            {confirmError}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1,
        }}
      >
        <Button
          color="inherit"
          onClick={onClose}
          disabled={confirmLoading}
          data-testid={closeoutTestId('reopen-cancel')}
          sx={{ color: 'text.secondary' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={confirmLoading}
          data-testid={closeoutTestId('reopen-confirm')}
          sx={{
            textTransform: 'none',
          }}
        >
          {confirmLoading ? 'Reopening…' : 'Confirm & Reopen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReopenPeriodModal;
